import { z } from 'zod'

import { LocalizedMediaSchema, LocalizedSchema, SlugSchema } from './common'
import { OpeningHoursSchema } from './content'
import { PublicImageSchema } from './public'

// Locations of a business with more than one place (package flag `multi_location`). Stored as one site
// document; each published location gets its own page and LocalBusiness markup.

export const LocationAddressSchema = z.object({
  street: z.string().max(200),
  city: z.string().max(120),
  region: z.string().max(120),
  postalCode: z.string().max(20),
  country: z.string().max(120),
})

export const LocationSchema = z.object({
  id: z.string().min(1).max(64),
  /** URL segment: `/locations/<slug>`. */
  slug: SlugSchema,
  name: LocalizedSchema,
  /** Short text on the location's page and in the list. */
  summary: LocalizedSchema.default({}),
  published: z.boolean().default(true),
  address: LocationAddressSchema,
  phone: z.string().max(40).default(''),
  email: z.string().max(254).default(''),
  /** Link to the place in a map app; shown as a button on the page. */
  mapUrl: z.union([z.literal(''), z.url().max(500).refine((u) => u.startsWith('https://'), 'Use an https link')]).default(''),
  geo: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).nullable().default(null),
  hours: OpeningHoursSchema,
  image: LocalizedMediaSchema.default({}),
})
export type Location = z.infer<typeof LocationSchema>

export const MAX_LOCATIONS = 50

export const LocationsSchema = z.object({
  items: z
    .array(LocationSchema)
    .max(MAX_LOCATIONS)
    .refine((l) => new Set(l.map((x) => x.slug)).size === l.length, 'Each location needs its own URL segment')
    .refine((l) => new Set(l.map((x) => x.id)).size === l.length, 'Duplicate location id'),
})
export type Locations = z.infer<typeof LocationsSchema>
export const emptyLocations = (): Locations => ({ items: [] })

/** A location as the public site reads it, resolved for one language. */
export const PublicLocationSchema = z.object({
  slug: z.string(),
  name: z.string(),
  summary: z.string(),
  address: LocationAddressSchema,
  phone: z.string(),
  email: z.string(),
  mapUrl: z.string(),
  geo: z.object({ lat: z.number(), lng: z.number() }).nullable(),
  hours: OpeningHoursSchema,
  image: PublicImageSchema.nullable(),
  updatedAt: z.string(),
})
export type PublicLocation = z.infer<typeof PublicLocationSchema>
